import moment from "moment";
import { Dispatch } from "redux";
import {
  clearWorkRequestMessage,
  setIsTicketsLoading,
  setTickets,
  setWorkOrderCount,
  setWorkRequestAlertVariant,
  setWorkRequestMessage,
} from "src/redux/actions/workRequest";
import { FirebaseDataService } from "src/services/Firebase/data";
import { RecurringScheduleItem, Ticket } from "src/types";
import { getBase64File } from "src/utils/convertToBase64";
import { roundTo2Decimals } from "src/utils/helpers";
import { ActiveEmployee, ActiveVendor } from "../AssignWOResource/types";
import { CloseWorkOrderFormValues } from "../CloseWorkOrderForm/types";
import { managerTableHeaders, tableHeaders, vendorTableHeaders } from "./constant";
import { WorkOrderDataParams, WorkOrderFunctionParams } from "./types";
import { setActiveResources } from "src/redux/actions/resources";
import { WORK_REQUEST_TYPE } from "src/pages/WorkRequests/constants";

const normalizeStatus = (value?: string) => (value ?? "").toLowerCase().replace(/[_\s-]+/g, "");

const toBool = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  }
  return false;
};

const readStringField = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (value == null) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const readNumberField = (source: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = source[key];
    if (value == null || value === "") continue;
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const getRecurringDisplayWonum = (scheduleId: string, linkedWonum: string) => {
  if (linkedWonum) return linkedWonum;

  const compact = scheduleId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const shortCode = compact.slice(0, 8);
  return shortCode ? `PM-RS-${shortCode}` : "PM-RECURRING";
};

const extractScheduledItems = (row: Record<string, unknown>): RecurringScheduleItem[] => {
  const raw = row.scheduledItems ?? row.scheduled_items;
  if (!Array.isArray(raw)) return [];

  return raw.filter((item): item is RecurringScheduleItem => !!item && typeof item === "object");
};

const extractRecurringScheduleRows = (payload: unknown): Record<string, unknown>[] => {
  const toRows = (value: unknown): Record<string, unknown>[] => {
    if (Array.isArray(value)) {
      return value.filter((item): item is Record<string, unknown> => !!item && typeof item === "object");
    }

    if (value && typeof value === "object") {
      const objectValue = value as Record<string, unknown>;

      // Handle paginated/enveloped responses like { items: [...] }.
      if (Array.isArray(objectValue.items)) {
        return toRows(objectValue.items);
      }

      return Object.entries(objectValue)
        .filter(([, item]) => !!item && typeof item === "object")
        .map(([id, item]) => ({ id, ...(item as Record<string, unknown>) }));
    }

    return [];
  };

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const objectPayload = payload as Record<string, unknown>;
  const candidates = [
    objectPayload.data,
    objectPayload.schedules,
    (objectPayload.data as Record<string, unknown> | undefined)?.schedules,
    (objectPayload.result as Record<string, unknown> | undefined)?.data,
    payload,
  ];

  for (const candidate of candidates) {
    const rows = toRows(candidate);
    if (rows.length) return rows;
  }

  return [];
};

const mapRecurringScheduleToTicket = (row: Record<string, unknown>): Ticket => {
  const scheduleId = readStringField(row, ["id", "scheduleId", "schedule_id"]);
  const linkedWonum = readStringField(row, [
    "linkedWonum",
    "linked_wonum",
    "wonum",
    "workOrderNumber",
    "work_order_number",
    "lastWonum",
    "last_wonum",
  ]);
  const location = readStringField(row, ["locationName", "location_name", "locationLabel", "location_label"]);
  const status =
    readStringField(row, ["status", "statusDescription", "status_description"]) ||
    (toBool(row.isActive) || toBool(row.active) ? "Active" : "Scheduled");
  const frequency = readStringField(row, ["frequency", "scheduleFrequency", "schedule_frequency"]);
  const serviceType =
    readStringField(row, ["serviceType", "service_type", "serviceTypeName", "service_type_name"]) ||
    "Recurring Service";
  const description =
    readStringField(row, ["description", "title", "name", "workOrderName", "work_order_name"]) ||
    (frequency ? `Recurring Service (${frequency})` : "Recurring Service");
  const clientName = readStringField(row, ["client", "clientName", "client_name", "customerName", "customer_name"]);
  const clientId = readStringField(row, ["clientId", "client_id", "customerId", "customer_id"]);
  const priorityId = readNumberField(row, ["priorityId", "priority_id", "priority"]);
  const priorityName =
    readStringField(row, ["priorityName", "priority_name", "priorityDescription", "priority_description"]) || "";
  const dateCreated =
    readStringField(row, [
      "createdAt",
      "created_at",
      "nextRunAt",
      "next_run_at",
      "nextRunDate",
      "next_run_date",
      "startDate",
      "start_date",
    ]) || new Date().toISOString();
  const nextRunDate = readStringField(row, ["nextRunAt", "next_run_at", "nextRunDate", "next_run_date"]);
  const scheduledItems = extractScheduledItems(row);
  const displayWonum = getRecurringDisplayWonum(scheduleId, linkedWonum);

  return {
    wonum: linkedWonum || (scheduleId ? `PM-SCHEDULE-${scheduleId}` : `PM-SCHEDULE-${dateCreated}`),
    displayWonum,
    name: description,
    client: clientName || "",
    clientId: clientId || "",
    dateCreated,
    location: location || "No location specified",
    description,
    serviceType,
    classification: "Preventative Maintenance",
    workType: "Preventative Maintenance",
    priority: priorityName,
    status,
    statusDescription: status,
    priorityId: priorityId ?? 0,
    clientUserId: "",
    woacceptscharges: false,
    approvedamount: undefined,
    pmnum: readStringField(row, ["pmnum", "pmNum", "pm_num"]) || scheduleId || undefined,
    linkedWonum: linkedWonum || undefined,
    isRecurring: true,
    recurringFrequency: frequency || undefined,
    nextRunDate: nextRunDate || undefined,
    workRequestMode: "RECURRING_SERVICE",
    workRequestType: "PREVENTATIVE_MAINTENANCE",
    recurringScheduleId: scheduleId || undefined,
    scheduledItems,
  } as Ticket;
};

const matchesKeyword = (ticket: Ticket, keyword: string) => {
  const query = keyword.trim().toLowerCase();
  if (!query) return true;

  const ticketWithMeta = ticket as Ticket & { recurringScheduleId?: string };
  const fields = [
    ticket.wonum,
    ticket.client,
    ticket.location,
    ticket.description,
    ticket.serviceType,
    ticket.classification,
    ticket.statusDescription,
    ticket.status,
    ticket.pmnum,
    ticketWithMeta.recurringScheduleId,
  ];

  return fields.some((field) =>
    String(field || "")
      .toLowerCase()
      .includes(query),
  );
};

const isPreventativeMaintenanceWorkOrder = (ticket: Ticket) => {
  const ticketWithRecurringFields = ticket as Ticket & Record<string, unknown>;
  const isRecurringFlag =
    toBool(ticketWithRecurringFields.isRecurring) ||
    toBool(ticketWithRecurringFields.is_recurring) ||
    toBool(ticketWithRecurringFields.isrecurring);

  const workRequestMode = normalizeStatus(
    readStringField(ticketWithRecurringFields, ["workRequestMode", "work_request_mode", "workrequestmode"]),
  );

  const workRequestType = normalizeStatus(
    readStringField(ticketWithRecurringFields, ["workRequestType", "work_request_type", "workrequesttype"]),
  );

  const hasRecurringSchedule =
    Boolean(ticketWithRecurringFields.recurringSchedule) || Boolean(ticketWithRecurringFields.recurring_schedule);

  const hasPmIdentifier =
    Boolean(ticket.pmnum) ||
    Boolean(ticketWithRecurringFields.pmNum) ||
    Boolean(ticketWithRecurringFields.pmnextduedate) ||
    Boolean(ticketWithRecurringFields.pmduedate);

  return (
    hasPmIdentifier ||
    isRecurringFlag ||
    hasRecurringSchedule ||
    workRequestMode === "recurringservice" ||
    workRequestType === "preventativemaintenance"
  );
};

const isInspectionWorkOrder = (ticket: Ticket) => {
  const ticketWithWorkType = ticket as Ticket & Record<string, unknown>;
  const workType = normalizeStatus(readStringField(ticketWithWorkType, ["workType", "work_type", "worktype"]));

  return workType === "inspection" || workType === "inspections" || workType === "inp" || workType === "insp";
};

const getBackendWorkRequestTypes = (workRequestType: string): string[] => {
  switch (workRequestType) {
    case WORK_REQUEST_TYPE.NEW:
    case WORK_REQUEST_TYPE.IN_PROGRESS:
    case WORK_REQUEST_TYPE.REQUIRES_APPROVAL:
    case WORK_REQUEST_TYPE.COMPLETED:
      return [
        WORK_REQUEST_TYPE.ALL,
        WORK_REQUEST_TYPE.OPEN,
        WORK_REQUEST_TYPE.APPROVAL_REQUIRED,
        WORK_REQUEST_TYPE.PLANNED,
        WORK_REQUEST_TYPE.COMPLETED,
      ];
    case WORK_REQUEST_TYPE.INSPECTIONS:
      return [
        WORK_REQUEST_TYPE.OPEN,
        WORK_REQUEST_TYPE.APPROVAL_REQUIRED,
        WORK_REQUEST_TYPE.PLANNED,
        WORK_REQUEST_TYPE.COMPLETED,
      ];
    case WORK_REQUEST_TYPE.PREVENTATIVE_MAINTENANCE:
      return [WORK_REQUEST_TYPE.OPEN, WORK_REQUEST_TYPE.APPROVAL_REQUIRED, WORK_REQUEST_TYPE.PLANNED];
    case WORK_REQUEST_TYPE.ALL:
      return [
        WORK_REQUEST_TYPE.OPEN,
        WORK_REQUEST_TYPE.APPROVAL_REQUIRED,
        WORK_REQUEST_TYPE.PLANNED,
        WORK_REQUEST_TYPE.COMPLETED,
      ];
    default:
      return [workRequestType];
  }
};

const getStatusCode = (ticket: Ticket) =>
  String(ticket.status || "")
    .trim()
    .toUpperCase();

const getNormalizedStatusLabel = (ticket: Ticket) => normalizeStatus(ticket.statusDescription || ticket.status);

const APPROVAL_STATUS_CODES = new Set(["APPREQ", "W-CL-APPR", "WAPPR"]);
const COMPLETED_STATUS_CODES = new Set(["COMP", "CAN", "CLOSE", "CLOSED", "TECH-COMP"]);

const isNewStatus = (ticket: Ticket) => {
  const statusCode = getStatusCode(ticket);
  const normalizedLabel = getNormalizedStatusLabel(ticket);
  return statusCode === "NEW" || normalizedLabel === "new";
};

const isRequiresApprovalStatus = (ticket: Ticket) => {
  const statusCode = getStatusCode(ticket);
  const normalizedLabel = getNormalizedStatusLabel(ticket);

  return (
    APPROVAL_STATUS_CODES.has(statusCode) ||
    normalizedLabel === "approvalrequired" ||
    normalizedLabel === "clientapprovalrequired"
  );
};

const isCompletedStatus = (ticket: Ticket) => {
  const statusCode = getStatusCode(ticket);
  const normalizedLabel = getNormalizedStatusLabel(ticket);

  return (
    COMPLETED_STATUS_CODES.has(statusCode) ||
    normalizedLabel.includes("closed") ||
    normalizedLabel.includes("completed") ||
    normalizedLabel.includes("cancelled") ||
    normalizedLabel.includes("canceled")
  );
};

const applyStatusFilter = (workOrders: Ticket[], workRequestType: string): Ticket[] => {
  if (workRequestType === WORK_REQUEST_TYPE.INSPECTIONS) {
    return workOrders.filter(isInspectionWorkOrder);
  }

  if (workRequestType === WORK_REQUEST_TYPE.PREVENTATIVE_MAINTENANCE) {
    return workOrders.filter(isPreventativeMaintenanceWorkOrder);
  }

  if (workRequestType === WORK_REQUEST_TYPE.NEW) {
    return workOrders.filter(isNewStatus);
  }

  if (workRequestType === WORK_REQUEST_TYPE.REQUIRES_APPROVAL) {
    return workOrders.filter(isRequiresApprovalStatus);
  }

  if (workRequestType === WORK_REQUEST_TYPE.IN_PROGRESS) {
    return workOrders.filter((ticket) => {
      return !isNewStatus(ticket) && !isRequiresApprovalStatus(ticket) && !isCompletedStatus(ticket);
    });
  }

  if (workRequestType === WORK_REQUEST_TYPE.COMPLETED) {
    return workOrders.filter(isCompletedStatus);
  }

  return workOrders;
};

const dedupeWorkOrders = (workOrders: Ticket[]) => {
  const byKey = new Map<string, Ticket>();
  workOrders.forEach((workOrder) => {
    const scheduleId = (workOrder as Ticket & { recurringScheduleId?: string }).recurringScheduleId;
    const fallbackKey = [workOrder.clientId, workOrder.description, workOrder.dateCreated].join("|");
    const key = workOrder.wonum || scheduleId || fallbackKey;
    byKey.set(key, workOrder);
  });
  return Array.from(byKey.values());
};

export const getApprovedAmount = (ticket: Ticket) => {
  if (ticket.woacceptscharges) {
    return ticket.approvedamount ? `$ ${roundTo2Decimals(ticket.approvedamount)}` : "Post-completion";
  } else {
    return "Non-billable";
  }
};

export const uploadImages = async (workOrderId: string, files: File[]) => {
  if (!files?.length) {
    return;
  }
  if (workOrderId) {
    for (const item of files) {
      const file = item;
      const folderName = "Technician";

      const convertedFile = (await getBase64File(file)) as string;
      const fileToUpload = convertedFile?.slice(convertedFile.indexOf(",") + 1);
      await FirebaseDataService.addMaximoWorkOrderAttachment(
        fileToUpload,
        workOrderId,
        file.name,
        file.type,
        folderName,
      );
    }
  }
};

export const getWorkOrders = async (
  {
    filters,
    isManager,
    isSearchJustApplied,
    loggedInUserId,
    pageNum,
    searchKeyword,
    vendorId,
    workRequestType,
    workRequestsPage,
    isRedirectToDetailPageAllowed,
  }: WorkOrderDataParams,
  { navigate, setWorkRequestsPage, dispatch }: WorkOrderFunctionParams,
) => {
  dispatch(setIsTicketsLoading(true));

  const isLocallyStatusFilteredTab =
    workRequestType === WORK_REQUEST_TYPE.NEW ||
    workRequestType === WORK_REQUEST_TYPE.IN_PROGRESS ||
    workRequestType === WORK_REQUEST_TYPE.REQUIRES_APPROVAL ||
    workRequestType === WORK_REQUEST_TYPE.COMPLETED;

  const requestedPageSize = Number(filters?.pageSize || 10);
  const safeRequestedPageSize = Number.isFinite(requestedPageSize) && requestedPageSize > 0 ? requestedPageSize : 10;
  const safeRequestedPageNum = Number(pageNum && pageNum > 0 ? pageNum : 1);

  const requestTypes = getBackendWorkRequestTypes(workRequestType);
  const responses = await Promise.all(
    requestTypes.map((requestType) =>
      FirebaseDataService.getMaximoWorkOrders(
        loggedInUserId,
        {
          pageNum: isLocallyStatusFilteredTab ? 1 : safeRequestedPageNum,
          orderBy: filters?.orderBy,
          pageSize: isLocallyStatusFilteredTab ? 1000 : safeRequestedPageSize,
          workRequestType: requestType,
        },
        isManager,
        searchKeyword || "",
        vendorId,
      ),
    ),
  );

  const allWorkOrders = responses.flatMap((response) => response.data?.workOrders ?? []);
  const uniqueWorkOrders = dedupeWorkOrders(allWorkOrders);
  let filteredWorkOrders = applyStatusFilter(uniqueWorkOrders, workRequestType);

  if (workRequestType === WORK_REQUEST_TYPE.PREVENTATIVE_MAINTENANCE) {
    try {
      const recurringResponse = await FirebaseDataService.getRecurringWorkOrderSchedules(
        loggedInUserId,
        isManager,
        vendorId,
      );
      const recurringRows = extractRecurringScheduleRows(recurringResponse);
      const recurringTickets = recurringRows.map(mapRecurringScheduleToTicket).filter((ticket) => {
        if (!searchKeyword?.trim()) return true;
        return matchesKeyword(ticket, searchKeyword);
      });

      filteredWorkOrders = dedupeWorkOrders([...filteredWorkOrders, ...recurringTickets]);
    } catch (error) {
      console.error("Failed to load recurring work order schedules:", error);
    }
  }

  if (workRequestType === WORK_REQUEST_TYPE.INSPECTIONS) {
    filteredWorkOrders = dedupeWorkOrders(filteredWorkOrders);
  }

  const primaryData = responses[0]?.data;
  const totalCountFromResponses = responses.reduce((sum, response) => {
    const count = Number(response?.data?.workOrderCount || 0);
    return sum + (Number.isFinite(count) ? count : 0);
  }, 0);

  const pageStart = (safeRequestedPageNum - 1) * safeRequestedPageSize;
  const pageEnd = pageStart + safeRequestedPageSize;
  const visibleWorkOrders = isLocallyStatusFilteredTab
    ? filteredWorkOrders.slice(pageStart, pageEnd)
    : filteredWorkOrders;

  let workOrderCount = isLocallyStatusFilteredTab
    ? filteredWorkOrders.length
    : requestTypes.length === 1
      ? Number(primaryData?.workOrderCount || filteredWorkOrders.length)
      : Math.max(totalCountFromResponses, filteredWorkOrders.length);

  if (workRequestType === WORK_REQUEST_TYPE.PREVENTATIVE_MAINTENANCE) {
    workOrderCount = filteredWorkOrders.length;
  }

  if (!isLocallyStatusFilteredTab && primaryData && primaryData.workOrderPage !== workRequestsPage) {
    setWorkRequestsPage(primaryData.workOrderPage);
  }

  dispatch(setTickets(mergeWorkRequestData(visibleWorkOrders)));
  dispatch(setIsTicketsLoading(false));
  dispatch(setWorkOrderCount(workOrderCount));
  dispatch(setActiveResources({ employees: primaryData?.employees, vendors: primaryData?.vendors }));
  if (!isRedirectToDetailPageAllowed) {
    return;
  }

  const { wonum } = filteredWorkOrders[0] || {};

  if (
    filteredWorkOrders?.length === 1 &&
    wonum?.toLowerCase() === searchKeyword?.toLowerCase() &&
    isSearchJustApplied
  ) {
    navigate(`/workrequests/${wonum}`, { state: { ticketNumber: wonum } });
  }
};

export const onSubmitCloseWorkOrderHandler = async (
  values: CloseWorkOrderFormValues,
  selectedWorkOrder: Ticket | undefined,
  maximoTimezone: string,
  dispatch: Dispatch,
  setSelectedTicketForCloseAction: React.Dispatch<React.SetStateAction<Ticket | undefined>>,
) => {
  let success = false;
  if (!selectedWorkOrder) {
    return success;
  }
  const actstart = moment(values.actstart).tz(maximoTimezone, true).format();
  const actfinish = moment(values.actfinish).tz(maximoTimezone, true).format();
  const { status, message, data } = await FirebaseDataService.closeWorkOrder(
    selectedWorkOrder.wonum,
    values.notes,
    actstart,
    actfinish,
    values.approvedamount,
  );

  if (status === "success") {
    await uploadImages(data.workOrderId, values.attachments);
    dispatch(setWorkRequestMessage(message));
    dispatch(setWorkRequestAlertVariant(status));
    success = true;
  } else {
    const msg = message.split("\n")[1];
    dispatch(setWorkRequestMessage(msg));
    dispatch(setWorkRequestAlertVariant("danger"));
  }
  setSelectedTicketForCloseAction(undefined);

  return success;
};

export const clearWorkRequestMessageTimeout = (dispatch: Dispatch) => {
  const timeout = 10000;
  setTimeout(() => {
    dispatch(clearWorkRequestMessage());
  }, timeout);
};

export const mergeWorkRequestData = (data: Ticket[]) => {
  return data?.map((workOrder: Ticket): Ticket => {
    const rawWorkOrder = workOrder as Ticket & Record<string, any>;
    const vendorFromWpservice = rawWorkOrder?.wpservice?.find((service: any) => service?.vendor)?.vendor;
    const laborFromWplabor = rawWorkOrder?.wplabor?.find((labor: any) => labor?.laborcode)?.laborcode;
    const assignedResourceName =
      rawWorkOrder?.assignedResourceName ||
      rawWorkOrder?.assigned_resource_name ||
      rawWorkOrder?.assignedEmployeeName ||
      rawWorkOrder?.assigned_employee_name ||
      rawWorkOrder?.laborname ||
      "";

    return {
      ...workOrder,
      vendorId: workOrder.vendorId || vendorFromWpservice,
      laborId: workOrder.laborId || laborFromWplabor,
      assignedResourceName,
      assignedEmployeeEmail:
        rawWorkOrder?.assignedEmployeeEmail || rawWorkOrder?.assigned_employee_email || rawWorkOrder?.email || "",
      dateCreated: moment(workOrder.dateCreated).format("MMMM Do YYYY, h:mm:ss a"),
      location: workOrder.location || "No location specified",
    };
  });
};

export const getTableHeader = (isManager: boolean, isVendor: boolean) => {
  if (isManager) {
    return managerTableHeaders;
  } else if (isVendor) {
    return vendorTableHeaders;
  } else {
    return tableHeaders;
  }
};

export const getResourseData = (
  vendorId: string | undefined,
  laborCode: string | undefined,
  vendors?: ActiveVendor[],
  employees?: ActiveEmployee[],
): string | undefined => {
  const safeVendors = Array.isArray(vendors) ? vendors : [];
  const safeEmployees = Array.isArray(employees) ? employees : [];

  if (vendorId) {
    const vendor = safeVendors.find((v) => v.vendorId === vendorId);
    return vendor?.displayName || vendorId;
  } else if (laborCode) {
    const employee = safeEmployees.find((e) => e.laborCode === laborCode);
    return employee?.displayName || laborCode;
  } else {
    return;
  }
};
